import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import {
  BulkCreateDataUpdateRequestPayload,
  BulkTarget,
  DEFAULT_REQUEST_MOTIVO,
  RequestableField,
  UserForRequest,
} from '../../../interfaces/data-update-request.interface';
import {
  DataUpdateRequestService,
  ListUsersForRequestFilters,
} from '../../../services/data-update-request.service';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import {
  FILE_FIELDS,
  findPhonePrefix,
  formatDateForDisplay,
  humanizeFieldKey,
  hydrateCurrentDocumentUrls,
  isDateFieldKey,
  pairedFieldOf,
  parseUsuarioId,
  PHONE_PREFIX_FIELD_KEY,
} from '../../data-update-request/data-update-request.utils';

/**
 * Catálogo fallback alineado con `CreateDataUpdateRequestComponent`.
 * Si backend expone `/fields`, se sobreescribe.
 * `fechaExp` (vencimiento del documento) queda excluido: se deriva del
 * OCR al re-subir la foto frontal.
 */
const FALLBACK_FIELDS: RequestableField[] = [
  { fieldKey: 'nacionalidad', label: 'Nacionalidad', fieldType: 'text', group: 'personal' },
  { fieldKey: 'direccion', label: 'Dirección', fieldType: 'text', group: 'personal' },
  { fieldKey: 'ciudadResidencia', label: 'Ciudad de residencia', fieldType: 'text', group: 'personal' },
  { fieldKey: 'paisResidencia', label: 'País de residencia', fieldType: 'text', group: 'personal' },
  { fieldKey: 'prefijoTelefono', label: 'Prefijo telefónico', fieldType: 'phonePrefix', group: 'personal' },
  { fieldKey: 'telefono', label: 'Teléfono', fieldType: 'text', group: 'personal' },
  { fieldKey: 'fotoIdfrontal', label: 'Foto frontal', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'fotoIdPosterior', label: 'Foto reverso', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'selfie', label: 'Selfie del rostro', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'razonSocial', label: 'Razón social', fieldType: 'text', group: 'company', companyOnly: true },
  { fieldKey: 'rutEmpresa', label: 'RUT empresa', fieldType: 'text', group: 'company', companyOnly: true },
  { fieldKey: 'nombreComercial', label: 'Nombre comercial', fieldType: 'text', group: 'company', companyOnly: true },
];

type Step = 'select' | 'matrix';

/** Clave estable del estado de compliance (filtro, chip y orden). */
type ClaveCompliance =
  | 'aprobado'
  | 'por_aprobar'
  | 'rechazado'
  | 'blacklist'
  | 'bloqueado'
  | 'desconocido';

/** `estado_compliance_id` → clave. */
const CLAVE_POR_ESTADO: Record<number, ClaveCompliance> = {
  0: 'por_aprobar',
  1: 'rechazado',
  2: 'blacklist',
  3: 'aprobado',
  4: 'bloqueado',
};

/**
 * Página dedicada para creación masiva de solicitudes. Dos pasos:
 *  1. `select`: tabla paginada de usuarios con checkboxes y selección
 *     que persiste entre páginas (Map<usuarioId, UserForRequest>).
 *  2. `matrix`: filas = usuarios seleccionados, columnas = campos del
 *     catálogo. Por cada celda, dato actual + checkbox. Headers de
 *     columna con toggle tri-estado (vacío/parcial/lleno). Plazo y
 *     motivo comunes. Submit a `POST /data-update-requests/bulk`
 *     (todo-o-nada).
 */
@Component({
  selector: 'app-bulk-create-data-update-request',
  templateUrl: './bulk-create-data-update-request.component.html',
  styleUrls: ['./bulk-create-data-update-request.component.sass'],
})
export class BulkCreateDataUpdateRequestComponent implements OnInit {
  step: Step = 'select';

  // ── Paso 1: selección de usuarios ─────────────────────────────────
  searchQuery: string = '';
  rows: UserForRequest[] = [];
  page: number = 1;
  limit: number = 20;
  total: number = 0;
  totalPages: number = 0;
  loading: boolean = false;
  errorMessage: string | null = null;

  /**
   * Map<usuarioId, user> con los usuarios seleccionados. Usamos Map en
   * lugar de Set para persistir el `UserForRequest` completo y no tener
   * que re-fetchearlos cuando el operador cambia de página (la fila
   * concreta puede ya no estar en `rows`).
   */
  selected = new Map<number, UserForRequest>();

  // ── Paso 2: matriz ─────────────────────────────────────────────────
  allFields: RequestableField[] = [];

  /**
   * Selección por usuario en la matriz. Map<usuarioId, Set<fieldKey>>.
   * Persiste tildes individuales y agregadas (toggle por columna).
   */
  matrixSelection = new Map<number, Set<string>>();

  deadlineAt: string = this.defaultDeadline();
  motivo: string = DEFAULT_REQUEST_MOTIVO;
  saving: boolean = false;
  /** Errores por usuario devueltos por el backend tras un 400 todo-o-nada. */
  perUserErrors = new Map<number, string>();

  /**
   * Loading dedicado a la firma de URLs de documentos al pasar a la
   * matriz. Distinto de `loading` (que es para el listado).
   */
  preparingMatrix: boolean = false;

  /**
   * Contador monotónico para descartar responses de `loadUsers()`
   * obsoletos. El operador puede cambiar varios filtros rápido; sin
   * esto, si el request lento llega DESPUÉS del rápido, la vista queda
   * mostrando datos que no corresponden al filtro actual.
   */
  private loadUsersSeq: number = 0;

  // ── Sort y filtros ─────────────────────────────────────────────────
  /**
   * Filtros y ordenamiento: se envían al backend como query params. El
   * backend debe aplicarlos server-side (ver instructivo en el .md); el
   * front adicionalmente aplica los mismos filtros/orden client-side
   * sobre `rows` para que si el backend todavía no los soporta, la UX
   * no quede rota — la vista filtra al menos la página visible.
   */
  filterCompliance: 'all' | ClaveCompliance = 'all';
  filterTipo: 'all' | 'persona' | 'empresa' = 'all';

  /** ISO YYYY-MM-DD. Rango de fecha de creación del usuario. */
  filterCreatedFrom: string = '';
  filterCreatedTo: string = '';

  /**
   * Documentos faltantes a filtrar. Vacío = sin filtro. Combinables
   * (AND). El valor `'any'` es exclusivo: "al menos un documento
   * faltante" — si el operador lo elige, se ignoran los otros.
   */
  filterMissingDocuments: Array<
    'fotoIdfrontal' | 'fotoIdPosterior' | 'selfie' | 'estatuto' | 'documentoIdTributario' | 'any'
  > = [];

  /**
   * Ordenamiento. `sortBy = null` = orden default del backend.
   * Al arrancar el listado, ordenamos por `usuarioId desc` — el operador
   * casi siempre quiere ver los últimos usuarios registrados primero.
   */
  sortBy: 'usuarioId' | 'nombre' | 'numeroId' | 'email' | 'compliance' | 'tipo' | 'createdAt' | null = 'usuarioId';
  sortOrder: 'asc' | 'desc' = 'desc';

  constructor(
    private service: DataUpdateRequestService,
    private router: Router,
    private s3UrlService: S3UrlService,
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.loadFields();
  }

  // ── Paso 1: tabla de usuarios ─────────────────────────────────────

  async loadUsers(): Promise<void> {
    const seq = ++this.loadUsersSeq;
    this.loading = true;
    this.errorMessage = null;
    try {
      // Un input puramente numérico se interpreta como `usuarioId`
      // (búsqueda exacta), cualquier otra cosa como `q` (libre).
      const trimmed = this.searchQuery.trim();
      const usuarioId = trimmed ? parseUsuarioId(trimmed) : null;

      // `filterTipo` mapea a `tipoUsuarioId` que sí acepta el backend.
      let tipoUsuarioId: 0 | 1 | undefined;
      if (this.filterTipo === 'empresa') tipoUsuarioId = 1;
      else if (this.filterTipo === 'persona') tipoUsuarioId = 0;

      // `missingDocuments`: `any` es exclusivo (KYC incompleto);
      // ignora los otros valores si está presente.
      let missingDocuments: ListUsersForRequestFilters['missingDocuments'];
      if (this.filterMissingDocuments.includes('any')) {
        missingDocuments = 'any';
      } else if (this.filterMissingDocuments.length) {
        missingDocuments = this.filterMissingDocuments as Exclude<typeof missingDocuments, 'any' | undefined>;
      }

      // El sort por 'tipo' es puramente client-side (el backend no lo
      // acepta — para segmentar por tipo se usa el filtro
      // `tipoUsuarioId`). Cuando `sortBy = 'tipo'` no enviamos sort al
      // backend y el ordenamiento se aplica sobre `visibleRows`.
      const backendSortBy: ListUsersForRequestFilters['sortBy'] | undefined =
        this.sortBy && this.sortBy !== 'tipo' ? this.sortBy : undefined;

      const res = await this.service.listUsersForRequest({
        usuarioId: usuarioId ?? undefined,
        q: usuarioId === null && trimmed ? trimmed : undefined,
        page: this.page,
        limit: this.limit,
        tipoUsuarioId,
        estadoCompliance: this.filterCompliance === 'all' ? undefined : this.filterCompliance,
        createdFrom: this.filterCreatedFrom || undefined,
        createdTo: this.filterCreatedTo || undefined,
        missingDocuments,
        sortBy: backendSortBy,
        sortOrder: backendSortBy ? this.sortOrder : undefined,
        // En la creación masiva no ofrecemos usuarios que ya tienen una
        // solicitud activa (una solicitud abierta por usuario). El
        // backend excluye server-side; `visibleRows` refuerza client-side
        // por si el backend todavía no soporta este filtro.
        hasActiveRequest: false,
      });

      // Descartamos la respuesta si en el ínterin se disparó otro
      // `loadUsers()` (el operador cambió un filtro mientras esperábamos).
      // Sin esto, un request lento que llegue DESPUÉS del rápido dejaría
      // la vista mostrando datos que no corresponden al filtro actual.
      if (seq !== this.loadUsersSeq) return;

      this.rows = res.data ?? [];
      this.total = res.meta?.total ?? this.rows.length;
      // Fallback: si el backend no manda `totalPages`, lo calculamos a
      // partir de `total/limit`. Sin esto los botones de paginación
      // quedan disabled porque `page >= undefined` evalúa raro.
      const fromMeta = res.meta?.totalPages;
      this.totalPages =
        typeof fromMeta === 'number' && fromMeta > 0
          ? fromMeta
          : Math.max(1, Math.ceil(this.total / this.limit));
      // Corrección defensiva: si el backend devolvió menos páginas de
      // las esperadas (ej. tras cambiar filtros que reducen el total),
      // `this.page` puede haber quedado fuera de rango. Lo clampeamos y
      // recargamos — la nueva request incrementa `loadUsersSeq` y este
      // response no vuelve a aplicarse (evita loop infinito).
      if (this.totalPages > 0 && this.page > this.totalPages) {
        this.page = this.totalPages;
        this.loadUsers();
      }
    } catch (err: any) {
      if (seq !== this.loadUsersSeq) return;
      this.errorMessage =
        err?.error?.message || err?.message || 'No se pudo cargar el listado.';
    } finally {
      // Sólo el último request en flight es quien apaga el spinner. Si
      // ya hubo un `loadUsers()` más nuevo, dejamos que ese controle el
      // loading state.
      if (seq === this.loadUsersSeq) {
        this.loading = false;
      }
    }
  }

  applySearch(): void {
    this.page = 1;
    this.loadUsers();
  }

  /**
   * Vuelve a page=1 y recarga. Se llama desde cualquier cambio de
   * filtro (compliance, tipo, fechas, missing docs) para que la
   * paginación siempre esté consistente con el filtro actual.
   */
  applyFilters(): void {
    this.page = 1;
    this.loadUsers();
  }

  /** Resetea todos los filtros a su estado inicial y recarga. */
  resetFilters(): void {
    this.filterCompliance = 'all';
    this.filterTipo = 'all';
    this.filterCreatedFrom = '';
    this.filterCreatedTo = '';
    this.filterMissingDocuments = [];
    this.sortBy = 'usuarioId';
    this.sortOrder = 'desc';
    this.applyFilters();
  }

  /**
   * `true` cuando el operador tiene al menos un filtro activo o cambió
   * el orden respecto al default (`usuarioId desc`). Se usa para
   * mostrar el botón "Limpiar filtros" en la toolbar — que también
   * resetea el sort a su valor inicial.
   */
  get hasActiveFilters(): boolean {
    const sortIsDefault = this.sortBy === 'usuarioId' && this.sortOrder === 'desc';
    return (
      this.filterCompliance !== 'all' ||
      this.filterTipo !== 'all' ||
      !!this.filterCreatedFrom ||
      !!this.filterCreatedTo ||
      this.filterMissingDocuments.length > 0 ||
      !sortIsDefault
    );
  }

  /**
   * `YYYY-MM-DD` de hoy para bindear como `[max]` de los inputs de
   * fecha — no tiene sentido filtrar por usuarios creados en el futuro.
   */
  get todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  /**
   * Toggle de un valor en `filterMissingDocuments`. Si el operador
   * activa `'any'`, limpiamos los otros (son excluyentes por semántica).
   * Idem: si activa un doc específico, sacamos `'any'`.
   */
  toggleMissingDoc(
    doc: 'fotoIdfrontal' | 'fotoIdPosterior' | 'selfie' | 'estatuto' | 'documentoIdTributario' | 'any',
  ): void {
    const idx = this.filterMissingDocuments.indexOf(doc);
    if (idx >= 0) {
      this.filterMissingDocuments.splice(idx, 1);
    } else {
      if (doc === 'any') {
        this.filterMissingDocuments = ['any'];
      } else {
        this.filterMissingDocuments = this.filterMissingDocuments.filter((d) => d !== 'any');
        this.filterMissingDocuments.push(doc);
      }
    }
    this.applyFilters();
  }

  isMissingDocSelected(
    doc: 'fotoIdfrontal' | 'fotoIdPosterior' | 'selfie' | 'estatuto' | 'documentoIdTributario' | 'any',
  ): boolean {
    return this.filterMissingDocuments.includes(doc);
  }

  // ── Paginación ────────────────────────────────────────────────
  // Los cuatro navegadores comparten guardas: no permitimos disparar
  // otro `loadUsers()` mientras hay uno en curso (evita race conditions
  // que dejaban la UI con paginación desincronizada del `page`).

  get canGoPrev(): boolean {
    return this.page > 1 && !this.loading;
  }
  get canGoNext(): boolean {
    return this.page < this.totalPages && !this.loading;
  }

  nextPage(): void {
    if (!this.canGoNext) return;
    this.page++;
    this.loadUsers();
  }
  prevPage(): void {
    if (!this.canGoPrev) return;
    this.page--;
    this.loadUsers();
  }
  firstPage(): void {
    if (!this.canGoPrev) return;
    this.page = 1;
    this.loadUsers();
  }
  lastPage(): void {
    if (!this.canGoNext) return;
    this.page = this.totalPages;
    this.loadUsers();
  }

  /**
   * Opciones para el selector "resultados por página". 20 sigue siendo
   * el default; 100 y 200 son útiles cuando el operador quiere ordenar
   * / filtrar sobre un dataset amplio sin paginar tanto (el orden y
   * filtro son client-side sobre la página).
   */
  readonly pageSizeOptions = [20, 50, 100, 200];

  /**
   * Aplica un nuevo tamaño de página. Volvemos a page=1 para no quedar
   * en una página inexistente cuando el `limit` cambia (ej. estaba en
   * page 5 con limit 20 y el operador sube a 100 → page 5 ya no existe).
   * Valida que el nuevo valor esté en `pageSizeOptions` — si no, cae al
   * limit actual (ej. si un select rompe y devuelve un valor raro).
   */
  changePageSize(newLimit: number): void {
    const parsed = Number(newLimit);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    if (!this.pageSizeOptions.includes(parsed)) return;
    if (parsed === this.limit) return;
    this.limit = parsed;
    this.page = 1;
    this.loadUsers();
  }

  // ── Compliance / sort / filter helpers ───────────────────────────

  /**
   * Normaliza el estado compliance del usuario a una clave estable —
   * la usamos tanto para filtrar como para renderizar el chip. Cubre
   * los tres shapes que devuelven los endpoints del proyecto:
   *   - `estadoComplianceId` (numérico moderno)
   *   - `estadoComplianceID` (numérico legacy CamelCase)
   *   - `estadoCompliance` (string label u objeto anidado)
   *
   * Catálogo `estado_compliance` (el mismo que envían los botones de
   * compliance: rechazar = 1, blacklist = 2, aprobar = 3):
   *   0 → POR APROBAR
   *   1 → RECHAZADO
   *   2 → BLACKLIST
   *   3 → APROBADO
   *   4 → BLOQUEADO (antes SUSPENDIDO)
   * Antes acá el 1 era "por aprobar", el 2 "rechazado" y el 0 quedaba
   * "sin dato".
   */
  complianceKey(u: UserForRequest): ClaveCompliance {
    const id =
      typeof u.estadoComplianceId === 'number'
        ? u.estadoComplianceId
        : typeof u.estadoComplianceID === 'number'
          ? u.estadoComplianceID
          : null;
    if (id !== null) return CLAVE_POR_ESTADO[id] ?? 'desconocido';
    const ec = u.estadoCompliance;
    if (typeof ec === 'string') {
      const s = ec.trim().toUpperCase();
      if (s === 'APROBADO' || s === 'ACTIVO') return 'aprobado';
      if (s === 'POR APROBAR' || s === 'PENDIENTE') return 'por_aprobar';
      if (s === 'RECHAZADO') return 'rechazado';
      if (s === 'BLACKLIST') return 'blacklist';
      if (s === 'BLOQUEADO' || s === 'SUSPENDIDO') return 'bloqueado';
    }
    if (ec && typeof ec === 'object' && typeof ec.estadoID === 'number') {
      return CLAVE_POR_ESTADO[ec.estadoID] ?? 'desconocido';
    }
    return 'desconocido';
  }

  complianceLabel(u: UserForRequest): string {
    switch (this.complianceKey(u)) {
      case 'aprobado':
        return 'Aprobado';
      case 'por_aprobar':
        return 'Por aprobar';
      case 'rechazado':
        return 'Rechazado';
      case 'blacklist':
        return 'Blacklist';
      case 'bloqueado':
        return 'Bloqueado';
      default:
        return 'Sin dato';
    }
  }

  /**
   * Alterna el orden de una columna: none → asc → desc → none. Recarga
   * el listado — con el backend nuevo, esto ordena todo el dataset y
   * respeta la paginación. Con el backend viejo (que ignora sortBy),
   * el orden client-side sobre `visibleRows` mantiene la UX consistente.
   */
  toggleSort(column: NonNullable<this['sortBy']>): void {
    if (this.sortBy !== column) {
      this.sortBy = column;
      this.sortOrder = 'asc';
    } else if (this.sortOrder === 'asc') {
      this.sortOrder = 'desc';
    } else {
      // Estaba desc → limpio (mantiene orden default del backend).
      this.sortBy = null;
      this.sortOrder = 'asc';
    }
    this.page = 1;
    this.loadUsers();
  }

  sortIndicator(column: NonNullable<this['sortBy']>): 'asc' | 'desc' | null {
    return this.sortBy === column ? this.sortOrder : null;
  }

  /**
   * `rows` con filtros y ordenamiento aplicados. Client-side: sólo
   * actúa sobre la página visible. Si el operador quiere ordenar /
   * filtrar todo el dataset, tiene que combinar la búsqueda por texto
   * con la paginación — mostramos un hint en la toolbar avisando.
   */
  get visibleRows(): UserForRequest[] {
    // Excluimos a los usuarios que ya tienen una solicitud activa — en la
    // creación masiva no deben aparecer (se maneja una solicitud abierta
    // por usuario). Es la red de seguridad por si el backend devolviera
    // alguno pese al filtro `hasActiveRequest: false`.
    let out = this.rows.filter((u) => !u.activeRequestId);
    if (this.filterCompliance !== 'all') {
      out = out.filter((u) => this.complianceKey(u) === this.filterCompliance);
    }
    if (this.filterTipo !== 'all') {
      const wantCompany = this.filterTipo === 'empresa';
      out = out.filter((u) => {
        const tipo = u.tipoUsuarioId ?? u.tipoUsuarioID;
        return wantCompany ? tipo === 1 : tipo !== 1;
      });
    }
    if (this.sortBy) {
      const key = this.sortBy;
      const dir = this.sortOrder === 'asc' ? 1 : -1;
      // Copia — no mutamos `rows` (rompería la ref que consume el
      // template en `allVisibleSelected` cross-checkeo).
      out = [...out].sort((a, b) => this.compareBy(a, b, key) * dir);
    }
    return out;
  }

  private compareBy(
    a: UserForRequest,
    b: UserForRequest,
    key: NonNullable<this['sortBy']>,
  ): number {
    if (key === 'usuarioId') return (a.usuarioId ?? 0) - (b.usuarioId ?? 0);
    if (key === 'compliance') {
      // Orden lógico por severidad: aprobado > bloqueado > por_aprobar >
      // rechazado > blacklist > desconocido
      const rank: Record<ClaveCompliance, number> = {
        aprobado: 0,
        bloqueado: 1,
        por_aprobar: 2,
        rechazado: 3,
        blacklist: 4,
        desconocido: 5,
      };
      return rank[this.complianceKey(a)] - rank[this.complianceKey(b)];
    }
    if (key === 'tipo') {
      const ta = a.tipoUsuarioId ?? a.tipoUsuarioID ?? 0;
      const tb = b.tipoUsuarioId ?? b.tipoUsuarioID ?? 0;
      return ta - tb;
    }
    let va: string;
    let vb: string;
    if (key === 'nombre') {
      va = `${a.nombre ?? ''} ${a.apellidos ?? ''}`.trim().toLowerCase();
      vb = `${b.nombre ?? ''} ${b.apellidos ?? ''}`.trim().toLowerCase();
    } else {
      va = String((a as any)[key] ?? '').toLowerCase();
      vb = String((b as any)[key] ?? '').toLowerCase();
    }
    return va.localeCompare(vb);
  }

  isSelected(usuarioId: number): boolean {
    return this.selected.has(usuarioId);
  }

  toggleSelect(u: UserForRequest, event?: Event): void {
    event?.stopPropagation();
    if (this.selected.has(u.usuarioId)) {
      this.selected.delete(u.usuarioId);
    } else {
      this.selected.set(u.usuarioId, u);
    }
  }

  /**
   * ¿Todos los visibles (después de filtros/sort) están seleccionados?
   * Se calcula sobre `visibleRows` para que el checkbox del header
   * refleje el subset filtrado, no toda la página.
   */
  get allVisibleSelected(): boolean {
    const vs = this.visibleRows;
    return vs.length > 0 && vs.every((u) => this.selected.has(u.usuarioId));
  }

  /** ¿Algunos pero no todos visibles seleccionados? (indeterminate) */
  get someVisibleSelected(): boolean {
    const vs = this.visibleRows;
    return (
      vs.some((u) => this.selected.has(u.usuarioId)) &&
      !this.allVisibleSelected
    );
  }

  toggleSelectAllVisible(): void {
    const vs = this.visibleRows;
    if (this.allVisibleSelected) {
      for (const u of vs) this.selected.delete(u.usuarioId);
    } else {
      for (const u of vs) this.selected.set(u.usuarioId, u);
    }
  }

  clearSelection(): void {
    this.selected.clear();
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  async proceedToMatrix(): Promise<void> {
    if (this.selectedCount === 0) return;
    // Inicializa cada usuario con un Set vacío (si no existía).
    for (const id of this.selected.keys()) {
      if (!this.matrixSelection.has(id)) {
        this.matrixSelection.set(id, new Set());
      }
    }

    // Firma las URLs de documentos para previsualización. Sólo
    // procesamos los seleccionados (no toda la página). Si el usuario
    // ya tiene `currentDocumentUrls` poblado (caso futuro: backend lo
    // expone directo), `hydrateCurrentDocumentUrls` lo respeta y no
    // re-firma.
    this.preparingMatrix = true;
    try {
      await Promise.all(
        Array.from(this.selected.values()).map((u) =>
          hydrateCurrentDocumentUrls(u, this.s3UrlService),
        ),
      );
    } finally {
      this.preparingMatrix = false;
    }

    this.step = 'matrix';
  }

  backToSelect(): void {
    this.step = 'select';
    this.perUserErrors.clear();
  }

  // ── Paso 2: matriz ─────────────────────────────────────────────────

  private async loadFields(): Promise<void> {
    try {
      const fields = await this.service.getRequestableFields();
      const source =
        Array.isArray(fields) && fields.length ? fields : FALLBACK_FIELDS;
      // `fechaExp` se deriva del OCR del documento; no se ofrece como campo
      // solicitable aunque el backend siga devolviéndolo desde /fields.
      this.allFields = source.filter((f) => f.fieldKey !== 'fechaExp');
    } catch {
      this.allFields = FALLBACK_FIELDS;
    }
  }

  /** Usuarios seleccionados — orden estable para iteración del template. */
  get selectedUsers(): UserForRequest[] {
    return Array.from(this.selected.values());
  }

  /** ¿Una celda puntual está marcada? */
  isCellSelected(usuarioId: number, fieldKey: string): boolean {
    return this.matrixSelection.get(usuarioId)?.has(fieldKey) ?? false;
  }

  /**
   * Decide si la celda está habilitada para el usuario:
   *  - Campo empresa-only sólo aplica si el usuario es empresa (`1`).
   *  - Campo `requiresCompliance` (documentos) sólo si el usuario está
   *    aprobado, bloqueado o por aprobar (no a rechazados ni blacklist).
   *  - Resto → habilitado.
   */
  isCellDisabled(usuario: UserForRequest, field: RequestableField): boolean {
    if (field.companyOnly && !this.isCompany(usuario)) return true;
    if (field.requiresCompliance && !this.userHasCompliance(usuario)) return true;
    return false;
  }

  /**
   * Resuelve si el usuario es empresa aceptando los dos shapes que
   * usan los endpoints del proyecto (`tipoUsuarioId` camelCase y
   * `tipoUsuarioID` legacy). Devuelve `false` si no podemos determinarlo.
   */
  private isCompany(u: UserForRequest): boolean {
    if (typeof u.tipoUsuarioId === 'number') return u.tipoUsuarioId === 1;
    if (typeof u.tipoUsuarioID === 'number') return u.tipoUsuarioID === 1;
    return false;
  }

  /**
   * ¿Se le pueden pedir documentos? Aprobado, bloqueado (opera como
   * aprobado) o por aprobar. Igual que el backend, que lo vuelve a validar.
   */
  private userHasCompliance(u: UserForRequest): boolean {
    const clave = this.complianceKey(u);
    if (clave === 'rechazado' || clave === 'blacklist') return false;
    // Aprobado, bloqueado, por aprobar o estado desconocido (no bloqueamos
    // al operador: el backend responde si no corresponde).
    return true;
  }

  /**
   * Toggle de una celda. Si el field es parte de un par obligatorio
   * (teléfono ↔ prefijo), aplicamos la misma acción a su par para ese
   * mismo usuario — pedir el teléfono sin prefijo (o viceversa) no
   * tiene sentido operativo.
   *
   * Respetamos el gate de `isCellDisabled` del compañero: si por algún
   * motivo el field pareja no aplica al usuario (empresa-only,
   * compliance), no lo tocamos.
   */
  toggleCell(usuarioId: number, fieldKey: string): void {
    const nowSelected = !(this.matrixSelection.get(usuarioId)?.has(fieldKey) ?? false);
    this.applyCellSelection(usuarioId, fieldKey, nowSelected);

    const pair = pairedFieldOf(fieldKey);
    if (pair) {
      const user = this.selected.get(usuarioId);
      const field = user
        ? this.allFields.find((f) => f.fieldKey === pair)
        : null;
      if (user && field && !this.isCellDisabled(user, field)) {
        this.applyCellSelection(usuarioId, pair, nowSelected);
      }
    }
  }

  private applyCellSelection(
    usuarioId: number,
    fieldKey: string,
    selected: boolean,
  ): void {
    let set = this.matrixSelection.get(usuarioId);
    if (!set) {
      set = new Set();
      this.matrixSelection.set(usuarioId, set);
    }
    if (selected) set.add(fieldKey);
    else set.delete(fieldKey);
  }

  // ── Toggle por header de columna (tri-estado) ─────────────────────

  /**
   * Estado del header de columna:
   *  - 'empty': nadie marcó este field.
   *  - 'full': todos los usuarios habilitados lo tienen marcado.
   *  - 'partial': alguno sí, otros no (indeterminate).
   */
  columnState(field: RequestableField): 'empty' | 'partial' | 'full' {
    const eligible = this.selectedUsers.filter(
      (u) => !this.isCellDisabled(u, field),
    );
    if (eligible.length === 0) return 'empty';
    let marked = 0;
    for (const u of eligible) {
      if (this.matrixSelection.get(u.usuarioId)?.has(field.fieldKey)) marked++;
    }
    if (marked === 0) return 'empty';
    if (marked === eligible.length) return 'full';
    return 'partial';
  }

  /**
   * Toggle desde el header de columna. Aplica a todos los usuarios
   * elegibles de esa columna. Si el field está en un par obligatorio
   * (teléfono ↔ prefijo), también propagamos al field compañero para
   * cada usuario elegible en esa columna — así los dos siempre viajan
   * juntos en el payload al backend.
   */
  toggleColumn(field: RequestableField): void {
    const eligible = this.selectedUsers.filter(
      (u) => !this.isCellDisabled(u, field),
    );
    if (eligible.length === 0) return;
    const state = this.columnState(field);
    const turnOn = state !== 'full';
    for (const u of eligible) {
      this.applyCellSelection(u.usuarioId, field.fieldKey, turnOn);
    }

    const pairKey = pairedFieldOf(field.fieldKey);
    if (!pairKey) return;
    const pairField = this.allFields.find((f) => f.fieldKey === pairKey);
    if (!pairField) return;
    // Aplicamos al par siguiendo la misma dirección (turnOn) para los
    // usuarios que son elegibles en el field PAR (no en el original).
    for (const u of this.selectedUsers) {
      if (this.isCellDisabled(u, pairField)) continue;
      this.applyCellSelection(u.usuarioId, pairField.fieldKey, turnOn);
    }
  }

  // ── Display helpers ────────────────────────────────────────────────

  labelOfField(f: RequestableField): string {
    return f.label || humanizeFieldKey(f.fieldKey);
  }

  isFileField(fieldKey: string): boolean {
    return FILE_FIELDS.has(fieldKey);
  }

  /**
   * `true` si el field es un prefijo telefónico — la celda muestra
   * "Chile 🇨🇱 +56" en lugar del código pelado, alineado con la UX del
   * form del cliente.
   */
  isPhonePrefixField(fieldKey: string): boolean {
    return fieldKey === PHONE_PREFIX_FIELD_KEY;
  }

  /** Etiqueta bonita del prefijo actual del usuario: "Chile (+56)". */
  phonePrefixLabelFor(user: UserForRequest): string | null {
    const raw = user.prefijoTelefono ?? (user as any)['prefijoTelefono'];
    if (!raw) return null;
    const opt = findPhonePrefix(raw);
    return opt ? `${opt.name} (${opt.code})` : String(raw);
  }

  /** Path del asset de la bandera del prefijo actual, o null. */
  phonePrefixFlagFor(user: UserForRequest): string | null {
    const raw = user.prefijoTelefono ?? (user as any)['prefijoTelefono'];
    if (!raw) return null;
    const opt = findPhonePrefix(raw);
    return opt ? `assets/flags/${opt.flag}` : null;
  }

  /**
   * Devuelve el valor actual del usuario para un `fieldKey`. Lee primero
   * de `currentValues` (sub-objeto explícito si backend lo expone) y
   * cae a leer el campo en el root del usuario (caso real: el endpoint
   * `/users` devuelve `telefono`, `direccion`, etc. directo en el root).
   */
  currentValueOf(user: UserForRequest, fieldKey: string): string | null {
    const fromMap = user.currentValues?.[fieldKey];
    const v =
      fromMap !== undefined && fromMap !== null
        ? fromMap
        : (user as any)[fieldKey];
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    // Fechas: mostrar "DD/MM/YYYY" en lugar del ISO crudo — más
    // legible para el operador al armar la matriz.
    if (isDateFieldKey(fieldKey) && typeof v === 'string') {
      const formatted = formatDateForDisplay(v);
      if (formatted) return formatted;
    }
    return String(v);
  }

  currentDocUrlOf(user: UserForRequest, fieldKey: string): string | null {
    return user.currentDocumentUrls?.[fieldKey] ?? null;
  }

  // ── Lightbox para previsualizar documentos actuales ────────────────

  /** URL del documento mostrado en lightbox; `null` cuando está cerrado. */
  lightboxUrl: string | null = null;

  /**
   * Detecta si la URL pre-firmada apunta a una imagen. Para PDFs y
   * otros, abrimos en pestaña nueva en lugar del lightbox (no
   * renderizan bien en `<img>`).
   */
  isImageUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url);
  }

  /**
   * Abre el documento: lightbox si es imagen, nueva pestaña si es
   * PDF/otro. Importante: el llamador debe `stopPropagation` para que
   * el click no togglee el checkbox de la celda.
   */
  previewDocument(url: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.isImageUrl(url)) {
      this.lightboxUrl = url;
      document.body.style.overflow = 'hidden';
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    document.body.style.overflow = '';
  }

  // ── Submit todo-o-nada ────────────────────────────────────────────

  /** Cantidad de targets con al menos un fieldKey marcado. */
  get targetsWithFields(): number {
    let n = 0;
    for (const u of this.selectedUsers) {
      const set = this.matrixSelection.get(u.usuarioId);
      if (set && set.size > 0) n++;
    }
    return n;
  }

  /**
   * Subset de usuarios seleccionados a los que NO se les marcó ningún
   * campo. Estos se omiten del payload al backend — el operador los ve
   * en un banner para que decida si tildar algo o quitarlos de la
   * selección.
   */
  get usersWithoutFields(): UserForRequest[] {
    return this.selectedUsers.filter((u) => {
      const set = this.matrixSelection.get(u.usuarioId);
      return !set || set.size === 0;
    });
  }

  get hasUsersWithoutFields(): boolean {
    return this.usersWithoutFields.length > 0;
  }

  /** ¿El usuario actual no tiene ningún campo marcado? (para resaltar la fila). */
  isRowEmpty(usuarioId: number): boolean {
    const set = this.matrixSelection.get(usuarioId);
    return !set || set.size === 0;
  }

  /** Etiqueta amigable para mostrar en el banner ("Juan Pérez (#123)"). */
  displayUserShort(u: UserForRequest): string {
    const name = `${u.nombre || ''} ${u.apellidos || ''}`.trim() || u.email || `Usuario #${u.usuarioId}`;
    return `${name} (#${u.usuarioId})`;
  }

  async submitBulk(): Promise<void> {
    this.perUserErrors.clear();

    const targets: BulkTarget[] = [];
    const omitted: UserForRequest[] = [];
    for (const user of this.selectedUsers) {
      const fields = this.matrixSelection.get(user.usuarioId);
      if (!fields || fields.size === 0) {
        omitted.push(user);
        continue;
      }
      targets.push({
        usuarioId: user.usuarioId,
        items: Array.from(fields).map((fieldKey) => ({ fieldKey })),
      });
    }

    if (targets.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin destinatarios válidos',
        text: 'Tenés que marcar al menos un campo para al menos un usuario. Si no necesitás solicitar nada a un cliente, sacalo de la selección.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    // Si hay mezcla (algunos con campos, otros sin), confirmar al
    // operador qué usuarios van a ser omitidos. No mandamos solicitudes
    // vacías al backend — sólo los `targets` con items reales.
    if (omitted.length > 0) {
      const sample = omitted
        .slice(0, 5)
        .map((u) => `• ${this.displayUserShort(u)}`)
        .join('<br>');
      const extra =
        omitted.length > 5 ? `<br>...y ${omitted.length - 5} más.` : '';
      const proceed = await Swal.fire({
        icon: 'warning',
        title: 'Hay usuarios sin campos marcados',
        html: `
          <p style="margin:0 0 8px 0;">
            <strong>${omitted.length}</strong> usuario(s) seleccionado(s) no tienen ningún campo marcado y serán omitidos.
          </p>
          <div style="text-align:left; font-size:0.85rem; color:#374151; padding:10px 14px; background:#f9fafb; border-radius:8px;">
            ${sample}${extra}
          </div>
          <p style="margin:10px 0 0 0; font-size:0.85rem;">
            Se crearán <strong>${targets.length}</strong> solicitud(es) — sólo para los que sí tienen campos. ¿Continuar?
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: `Crear ${targets.length} solicitud(es)`,
        cancelButtonText: 'Volver a editar',
        confirmButtonColor: '#1e5a3f',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
      });
      if (!proceed.isConfirmed) return;
    }

    if (!this.deadlineAt) {
      Swal.fire({
        icon: 'warning',
        title: 'Falta el plazo',
        text: 'Definí una fecha límite para todas las solicitudes.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    if (!this.motivo.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Falta el motivo',
        text: 'Escribí un motivo que el cliente pueda leer.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    const payload: BulkCreateDataUpdateRequestPayload = {
      deadlineAt: this.normalizeDeadline(this.deadlineAt),
      motivo: this.motivo.trim(),
      targets,
    };

    this.saving = true;
    try {
      const res = await this.service.createBulkRequest(payload);
      await Swal.fire({
        icon: 'success',
        title: `${res.creadas} solicitud(es) creada(s)`,
        text: 'Los clientes ya pueden completar sus datos.',
        confirmButtonColor: '#1e5a3f',
      });
      this.router.navigate(['/wallet/admin/data-update-requests']);
    } catch (err: any) {
      // Backend devuelve 400 con `errores: [{ usuarioId, error }]` cuando
      // hay validación. Mostramos los errores inline en la matriz.
      const errors = err?.error?.errores as
        | { usuarioId: number; error: string }[]
        | undefined;
      if (Array.isArray(errors)) {
        for (const e of errors) this.perUserErrors.set(e.usuarioId, e.error);
        Swal.fire({
          icon: 'error',
          title: 'Validación fallida',
          text: 'Algunos usuarios tienen errores. Revisalos en la matriz; ninguna solicitud fue creada.',
          confirmButtonColor: '#1e5a3f',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'No pudimos crear las solicitudes',
          text: err?.error?.message || err?.message || 'Reintentá en unos segundos.',
          confirmButtonColor: '#1e5a3f',
        });
      }
    } finally {
      this.saving = false;
    }
  }

  errorForUser(usuarioId: number): string | null {
    return this.perUserErrors.get(usuarioId) ?? null;
  }

  cancel(): void {
    this.router.navigate(['/wallet/admin/data-update-requests']);
  }

  // ── Date helpers ─────────────────────────────────────────────────

  private defaultDeadline(): string {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }

  /**
   * Fecha de hoy en formato `YYYY-MM-DD` para bindear como `[min]` del
   * `<input type="date">`. Bloquea que el operador seleccione fechas
   * pasadas — el backend rechazaría con 400 y el UX quedaría opaco.
   */
  get todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private normalizeDeadline(value: string): string {
    return new Date(`${value}T23:59:00`).toISOString();
  }
}
